import { Component, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MessagesService } from "../../services/messages.service";
import { AuthenticationService } from "src/app/core/services/auth.service";
import { Subscription } from "rxjs";
import { SubLoaderModule } from "../sub-loader/sub-loader.module";
import { IBaseResponse } from "../../app/models/App/IBaseResponse";

@Component({
  selector: "app-change-password",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SubLoaderModule],
  template: `
    <app-sub-loader *ngIf="isLoading"></app-sub-loader>
    <form [formGroup]="formGroup" (ngSubmit)="submit()">
      <div class="modal-header">
        <h4 class="modal-title">Change Password</h4>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="activeModal.dismiss('Cross click')"
        ></button>
      </div>
      <div class="modal-body">
        <div
          class="alert alert-danger alert-sm"
          *ngIf="
            formGroup.hasError('passwordsNotMatched') &&
            f['confirmPassword'].dirty &&
            f['confirmPassword'].touched
          "
        >
          Passwords don't match!
        </div>
        <div class="mb-3">
          <label class="form-label font-weight-bold" for="password-input"
            >Password</label
          >
          <div class="position-relative auth-pass-inputgroup mb-3">
            <input
              [type]="fieldTextType ? 'text' : 'password'"
              class="form-control pe-5"
              id="password-input"
              formControlName="password"
              [ngClass]="{ 'is-invalid': submitted && f['password'].errors }"
            />
            <button
              class="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
              type="button"
              id="password-addon"
            >
              <i
                class="mdi align-middle"
                [ngClass]="{
                  'mdi-eye-off-outline': !fieldTextType,
                  'mdi-eye-outline': fieldTextType
                }"
                (click)="toggleFieldTextType()"
              ></i>
            </button>
            <small
              class="form-text"
              [ngClass]="{
                'text-danger': f['password'].touched && f['password'].invalid
              }"
            >
              Your password must be 8-20 characters long.
            </small>
            <div
              *ngIf="f['password'].touched && f['password'].errors"
              class="invalid-feedback d-flex flex-column"
            >
              <small *ngIf="f['password'].errors['required']">Required</small>
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label class="form-label font-weight-bold" for="confirmPassword-input"
            >Confirm Password</label
          >
          <div class="position-relative auth-pass-inputgroup mb-3">
            <input
              [type]="fieldTextType ? 'text' : 'password'"
              class="form-control pe-5"
              id="confirmPassword-input"
              formControlName="confirmPassword"
              [ngClass]="{
                'is-invalid': submitted && f['confirmPassword'].errors
              }"
            />
            <button
              class="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
              type="button"
              id="password-addon"
            >
              <i
                class="mdi align-middle"
                [ngClass]="{
                  'mdi-eye-off-outline': !fieldTextType,
                  'mdi-eye-outline': fieldTextType
                }"
                (click)="toggleFieldTextType()"
              ></i>
            </button>
            <small
              class="form-text"
              [ngClass]="{
                'text-danger':
                  f['confirmPassword'].touched && f['confirmPassword'].invalid
              }"
            >
              Your password must be 8-20 characters long.
            </small>
            <div
              *ngIf="
                f['confirmPassword'].touched && f['confirmPassword'].errors
              "
              class="invalid-feedback d-flex flex-column"
            >
              <small *ngIf="f['confirmPassword'].errors['required']"
                >Required</small
              >
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-sm btn-outline-danger"
          (click)="activeModal.dismiss('Close click')"
        >
          Close
        </button>
        <button type="submit" class="btn btn-sm btn-primary">Change</button>
      </div>
    </form>
  `,
  styles: [],
})
export class ChangePasswordComponent implements OnDestroy {
  formGroup!: FormGroup;
  submitted: boolean = false;
  isLoading: boolean = false;
  fieldTextType!: boolean;
  sub!: Subscription;
  constructor(
    public activeModal: NgbActiveModal,
    private message: MessagesService,
    private auth: AuthenticationService
  ) {
    this.formGroup = new FormGroup(
      {
        password: new FormControl(null, [
          Validators.required,
          Validators.pattern(`^[A-Za-z0-9!@#$%^&*(),.?":{}|<>]{8,20}$`),
        ]),
        confirmPassword: new FormControl(null, Validators.required),
      },
      { validators: this.confirmPasswordValidator }
    );
  }
  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }

  get f() {
    return this.formGroup.controls;
  }

  /**
   * Password Hide/Show
   */
  toggleFieldTextType() {
    this.fieldTextType = !this.fieldTextType;
  }

  confirmPasswordValidator(
    control: AbstractControl
  ): { [key: string]: any } | null {
    const password = control.get("password");
    const confirmPassword = control.get("confirmPassword");

    if (password?.value !== confirmPassword?.value) {
      return { passwordsNotMatched: true };
    }

    return null;
  }

  validationChecker(): boolean {
    if (this.formGroup.invalid) {
      this.message.popup(
        "Attention!",
        "Please Fill Required Inputs",
        "warning"
      );
      return false;
    }
    return true;
  }

  submit() {
    this.submitted = true;
    if (!this.validationChecker()) return;
    this.isLoading = true;
    this.sub = this.auth
      .changePassword(this.f["password"].value)
      .subscribe((res: IBaseResponse<number>) => {
        if (res.status) {
          this.submitted = false;
          this.formGroup.reset();
          this.activeModal.close();
          this.message.toast(res.message!, "success");
        } else this.message.popup("Oops!", res.message!, "error");
        this.isLoading = false;
      });
  }
}
